import { PrivateUserInfoType, Vehicle } from '@otw/models'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { db, storage } from 'config/firebase'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import {
  photoUploadClientConverter
} from '@otw/models/converters'
import { PhotoUploadClient } from '@otw/models/photos'
import { 
  FirestoreDataConverter, 
  DocumentSnapshot, 
  SnapshotOptions, 
  WithFieldValue 
} from 'firebase/firestore'
import { deletePhotoUpload } from '@/core/photoUploader/deletePhotoUpload'
import { useAccessCodes } from 'hooks/useAccessCodes'
import { collection, getDocs } from '@firebase/firestore'
import { listAll, ref } from '@firebase/storage'
import { useInventory } from 'hooks/inventory/useInventory'
import { useUsers } from 'hooks/useUsers'
import { useMemo } from 'react'
import { createPhotoUploadForUser } from '@/core/api/photoUploader/createPhotoUploadForUser'
import { AccessCode } from '@otw/models/photos'

// Clean interfaces for photo upload management
export interface PhotoUploadVehicle extends Omit<Vehicle, 'status'> {
  submissionStatus: 'Submitted' | 'Not submitted'
  hasSubmitted: boolean
  photoUploadIds: string[]
  photoCount?: number // Actual number of photos submitted for this vehicle
  videoCount?: number // Actual number of videos submitted for this vehicle
}

// User-centric aggregated data structure for the main table
export interface UserPhotoUploadSummary {
  user: PrivateUserInfoType
  vehicles: PhotoUploadVehicle[]
  submissionCount: number // Deprecated - kept for backward compatibility
  totalVehicles: number
  // New photo-based progress tracking
  actualPhotoCount: number // Total photos actually uploaded
  expectedPhotoCount: number // Total photos expected (vehicles * 14)
  photoProgress: number // Percentage based on actual photos
  vehiclesCompleteCount?: number // Vehicles fully submitted (14 photos + 1 video)
  overallStatus: 'Not submitted' | 'Partially submitted' | 'Fully submitted' | 'Complete'
  photoUploadDocument: PhotoUploadDocument
  photoUploadId: string
  accessCode?: string
  // Video tracking
  actualVideoCount: number // Total videos actually uploaded
  expectedVideoCount: number // Total videos expected (vehicles * 1)
  // Access code data for expiration checking
  accessCodes: Array<{
    id: string
    code: string
    expires: string
    createdAt: string
  }>
}

// Constants
const REQUIRED_PHOTOS_PER_VEHICLE = 14

// Photo Upload Document interface (keeping local for backward compatibility)
export interface PhotoUploadDocument {
  id: string
  userId: string
  vehicleIds: string[]
  submittedVehicles: string[]
  createdAt: string
  updatedAt: string
  status: 'active' | 'completed' | 'cancelled'
  accessCode?: string
  expiresAt?: string
  completedAt?: string
}

// Photo Upload Document Converter (keeping local for backward compatibility)
export const photoUploadDocumentConverter: FirestoreDataConverter<PhotoUploadDocument> = {
  toFirestore(photoUpload: WithFieldValue<PhotoUploadDocument>) {
    const { id, ...data } = photoUpload
    return {
      ...data,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      status: data.status || 'active',
      vehicleIds: data.vehicleIds || [],
      submittedVehicles: data.submittedVehicles || []
    }
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): PhotoUploadDocument {
    const data = snapshot.data(options)
    
    if (!data) {
      return {
        id: snapshot.id,
        userId: '',
        vehicleIds: [],
        submittedVehicles: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active'
      }
    }

    return {
      id: snapshot.id,
      userId: data.userId || '',
      vehicleIds: data.vehicleIds || [],
      submittedVehicles: data.submittedVehicles || [],
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      status: data.status || 'active',
      accessCode: data.accessCode || undefined,
      expiresAt: data.expiresAt || undefined,
      completedAt: data.completedAt || undefined
    }
  }
}

// Function to aggregate photo uploads by user
const aggregatePhotoUploadsByUser = (
  photoUploads: PhotoUploadDocument[],
  users: PrivateUserInfoType[],
  vehicles: Vehicle[],
  accessCodes: Array<AccessCode> = []
): UserPhotoUploadSummary[] => {
  // Create maps for efficient lookup
  const usersMap = new Map(users.map(user => [user.id, user]))
  const vehiclesMap = new Map(vehicles.map(vehicle => [vehicle.id, vehicle]))
  
  // Group access codes by photoUploadId for efficient lookup
  const accessCodesMap = new Map<string, Array<{id: string, code: string, expires: string, createdAt: string}>>()
  accessCodes.forEach(accessCode => {
    const existing = accessCodesMap.get(accessCode.photoUploadId) || []
    existing.push({
      id: accessCode.id,
      code: accessCode.code,
      expires: accessCode.expires,
      createdAt: accessCode.createdAt
    })
    accessCodesMap.set(accessCode.photoUploadId, existing)
  })
  
  // Group photo uploads by user - should be one per user
  const userPhotoUploadsMap = new Map<string, PhotoUploadDocument>()
  
  for (const photoUpload of photoUploads) {
    const userId = photoUpload.userId
    if (userPhotoUploadsMap.has(userId)) {
      console.warn(`Warning: User ${userId} has multiple photo upload documents. Using the latest one.`)
      const existing = userPhotoUploadsMap.get(userId)!
      const existingDate = new Date(existing.createdAt || 0)
      const currentDate = new Date(photoUpload.createdAt || 0)
      // Keep the latest one
      if (currentDate > existingDate) {
        userPhotoUploadsMap.set(userId, photoUpload)
      }
    } else {
      userPhotoUploadsMap.set(userId, photoUpload)
    }
  }

  // Create aggregated user summaries
  const userSummaries: UserPhotoUploadSummary[] = []

  for (const [userId, photoUploadDocument] of userPhotoUploadsMap) {
    const user = usersMap.get(userId)
    if (!user) continue // Skip if user not found

    // Get vehicles for this photo upload
    const photoUploadVehicles = photoUploadDocument.vehicleIds
      .map(vehicleId => {
        const vehicle = vehiclesMap.get(vehicleId)
        if (!vehicle) return null
        
        const hasSubmitted = photoUploadDocument.submittedVehicles?.includes(vehicleId) || false
        const { status, ...vehicleWithoutStatus } = vehicle
        
        return {
          ...vehicleWithoutStatus,
          submissionStatus: hasSubmitted ? 'Submitted' as const : 'Not submitted' as const,
          hasSubmitted,
          photoUploadIds: [photoUploadDocument.id],
          photoCount: 0 // Will be populated by separate photo count query
        }
      })
      .filter((vehicle): vehicle is NonNullable<typeof vehicle> => vehicle !== null)

    const submissionCount = photoUploadDocument.submittedVehicles?.length || 0
    const totalVehicles = photoUploadVehicles.length
    const expectedPhotoCount = totalVehicles * REQUIRED_PHOTOS_PER_VEHICLE

    // Calculate photo-based progress (initially 0, will be updated)
    const actualPhotoCount = 0 // Will be calculated separately to avoid performance issues
    const photoProgress = expectedPhotoCount > 0 ? Math.round((actualPhotoCount / expectedPhotoCount) * 100) : 0

    // Determine overall status based on photo completion
    let overallStatus: UserPhotoUploadSummary['overallStatus'] = 'Not submitted'
    
    if (photoUploadDocument.status === 'completed' && photoProgress === 100) {
      overallStatus = 'Complete'
    } else if (actualPhotoCount > 0) {
      if (photoProgress === 100) {
        overallStatus = 'Fully submitted'
      } else {
        overallStatus = 'Partially submitted'
      }
    }

    // Get access codes for this photo upload
    const photoUploadAccessCodes = accessCodesMap.get(photoUploadDocument.id) || []

    userSummaries.push({
      user,
      vehicles: photoUploadVehicles,
      submissionCount, // Keep for backward compatibility
      totalVehicles,
      actualPhotoCount,
      expectedPhotoCount,
      photoProgress,
      actualVideoCount: 0, // Will be calculated separately
      expectedVideoCount: totalVehicles * 1, // 1 video per vehicle
      overallStatus,
      photoUploadDocument,
      photoUploadId: photoUploadDocument.id,
      accessCode: photoUploadDocument.accessCode,
      accessCodes: photoUploadAccessCodes
    })
  }

  return userSummaries
}

// Function to fetch actual photo counts for photo uploads
const fetchPhotoCountsForSummaries = async (summaries: UserPhotoUploadSummary[]): Promise<UserPhotoUploadSummary[]> => {
  const updatedSummaries = await Promise.all(
    summaries.map(async (summary) => {
      try {
        // Fetch photo counts for each vehicle
        const vehiclePhotoCounts = await Promise.all(
          summary.vehicles.map(async (vehicle) => {
            try {
              const storagePath = `photoUploads/${summary.photoUploadId}/photos/${vehicle.id}`
              const storageRef = ref(storage, storagePath)
              const result = await listAll(storageRef)
              // Videos
              const videoPath = `photoUploads/${summary.photoUploadId}/videos/${vehicle.id}`
              const videoRef = ref(storage, videoPath)
              let videoCount = 0
              try {
                const videoRes = await listAll(videoRef)
                videoCount = videoRes.items?.length || 0
              } catch {
                videoCount = 0
              }
              return {
                ...vehicle,
                photoCount: result.items?.length || 0,
                videoCount,
              }
            } catch (err) {
              // If storage access fails, assume 0 photos
              return {
                ...vehicle,
                photoCount: 0,
                videoCount: 0,
              }
            }
          })
        )

        // Calculate totals
        const actualPhotoCount = vehiclePhotoCounts.reduce((sum, vehicle) => sum + (vehicle.photoCount || 0), 0)
        const expectedPhotoCount = summary.vehicles.length * REQUIRED_PHOTOS_PER_VEHICLE
        const photoProgress = expectedPhotoCount > 0 ? Math.round((actualPhotoCount / expectedPhotoCount) * 100) : 0

        // Calculate video totals
        const actualVideoCount = vehiclePhotoCounts.reduce((sum, vehicle) => sum + (vehicle.videoCount || 0), 0)
        const expectedVideoCount = summary.vehicles.length * 1 // 1 video per vehicle

        // Vehicles fully complete = 14 photos + at least 1 video
        const vehiclesCompleteCount = vehiclePhotoCounts.reduce((sum, v) => {
          const isComplete = (v.photoCount || 0) >= REQUIRED_PHOTOS_PER_VEHICLE && (v.videoCount || 0) >= 1
          return sum + (isComplete ? 1 : 0)
        }, 0)

        // Update overall status based on actual photo counts
        let overallStatus: UserPhotoUploadSummary['overallStatus'] = 'Not submitted'
        
        if (vehiclesCompleteCount === summary.totalVehicles && summary.totalVehicles > 0) {
          overallStatus = 'Complete'
        } else if (vehiclesCompleteCount > 0 || actualPhotoCount > 0) {
          overallStatus = 'Partially submitted'
        }

        return {
          ...summary,
          vehicles: vehiclePhotoCounts,
          actualPhotoCount,
          expectedPhotoCount,
          photoProgress,
          actualVideoCount,
          expectedVideoCount,
          vehiclesCompleteCount,
          overallStatus
        }
      } catch (err) {
        console.error(`Error fetching photo counts for user ${summary.user.email}:`, err)
        return {
          ...summary,
          actualVideoCount: 0,
          expectedVideoCount: summary.totalVehicles * 1
        } // Return original summary with video defaults if photo count fetching fails
      }
    })
  )

  return updatedSummaries
}

export const usePhotoUploads = () => {
  const queryClient = useQueryClient()
  const { showSuccess, showError, showWarning } = useSnackbar()

  // Use existing cached data hooks
  const { users, isLoading: isLoadingUsers } = useUsers()
  const { vehicles, isLoading: isLoadingVehicles } = useInventory('master_inventory')
  
  // Use access codes hook for access code operations
  const {
    accessCodes,
    isLoading: isLoadingAccessCodes,
    deleteAccessCode,
    deleteAllAccessCodesForPhotoUpload,
    isDeletingAccessCode,
    isDeletingAllForPhotoUpload,
    getAccessLink: getAccessLinkFromHook
  } = useAccessCodes()

  // Fetch photo upload documents
  const {
    data: rawPhotoUploads = [],
    isLoading: isLoadingPhotoUploads,
    error,
    refetch
  } = useQuery({
    queryKey: ['photoUploads-raw'],
    queryFn: async (): Promise<PhotoUploadDocument[]> => {
      const photoUploadsRef = collection(db, 'photoUploads').withConverter(photoUploadDocumentConverter)
      const photoUploadsSnapshot = await getDocs(photoUploadsRef)
      return photoUploadsSnapshot.docs.map(doc => doc.data())
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  })

  // Combine data using cached users and vehicles - user-centric summaries
  const userPhotoUploadSummaries = useMemo(() => {
    if (!users || !vehicles || !rawPhotoUploads.length) {
      return []
    }
    
    return aggregatePhotoUploadsByUser(rawPhotoUploads, users, vehicles, accessCodes)
  }, [rawPhotoUploads, users, vehicles, accessCodes])

  // Fetch photo counts and update summaries with accurate progress
  const {
    data: photoUploadSummariesWithCounts,
    isLoading: isLoadingPhotoCounts,
    error: photoCountsError
  } = useQuery({
    queryKey: ['photoUploads-with-counts', userPhotoUploadSummaries.map(s => s.photoUploadId).join(',')],
    queryFn: async () => {
      if (!userPhotoUploadSummaries.length) return []
      return await fetchPhotoCountsForSummaries(userPhotoUploadSummaries)
    },
    enabled: userPhotoUploadSummaries.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false, // Avoid excessive storage calls
  })

  // Use summaries with photo counts if available, otherwise use basic summaries
  const finalPhotoUploads = photoUploadSummariesWithCounts || userPhotoUploadSummaries

  // Combined loading state
  const isLoading = isLoadingPhotoUploads || isLoadingUsers || isLoadingVehicles || isLoadingAccessCodes

  // Create photo upload mutation
  const createPhotoUploadMutation = useMutation({
    mutationFn: async (vars: Parameters<typeof createPhotoUploadForUser>[0]) => {
      // Ensure we have a real, persisted user id before creating upload
      // Re-fetch user profile from cache; if missing id, throw to prevent optimistic creation
      const userId = vars.user?.id
      if (!userId) {
        throw new Error('User ID is missing. Please select a valid user before creating a photo upload.')
      }
      // Optionally, we could verify this id exists in users list
      return await createPhotoUploadForUser(vars)
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['photoUploads-raw'] })
      queryClient.invalidateQueries({ queryKey: ['master_inventory'] })
      
      if (data.hasPhone) {
        showSuccess(`Photo upload created and link sent to ${variables.user.email}`)
      } else {
        showWarning('Photo upload created successfully, but no phone number to send link')
      }
    },
    onError: (error) => {
      console.error('Error creating photo upload:', error)
      showError('Error creating photo upload')
    }
  })

  // Delete photo upload mutation
  const deletePhotoUploadMutation = useMutation({
    mutationFn: async (photoUploadId: string) => {
      await deletePhotoUpload(photoUploadId)
    },
    onMutate: async (photoUploadId) => {
      await queryClient.cancelQueries({ queryKey: ['photoUploads-raw'] })
      const previousData = queryClient.getQueryData<PhotoUploadDocument[]>(['photoUploads-raw'])
      
      queryClient.setQueryData<PhotoUploadDocument[]>(['photoUploads-raw'], (old) => 
        old?.filter(upload => upload.id !== photoUploadId) || []
      )
      
      return { previousData }
    },
    onError: (error, photoUploadId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['photoUploads-raw'], context.previousData)
      }
      console.error('Error deleting photo upload:', error)
      showError('Error deleting photo upload')
    },
    onSuccess: () => {
      showSuccess('Photo upload deleted successfully')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['photoUploads-raw'] })
    }
  })

  return {
    // Data for the main table/list
    photoUploads: finalPhotoUploads,
    isLoading,
    error,
    refetch,

    // Actions
    createPhotoUpload: createPhotoUploadMutation.mutate,
    deletePhotoUpload: deletePhotoUploadMutation.mutateAsync,
    deleteAccessCode,
    deleteAllAccessCodesForPhotoUpload,
    getAccessLink: getAccessLinkFromHook,

    // Loading states
    isCreatingPhotoUpload: createPhotoUploadMutation.isPending,
    isDeletingPhotoUpload: deletePhotoUploadMutation.isPending,
    isDeletingAccessCode,
    isDeletingAllAccessCodes: isDeletingAllForPhotoUpload,
  }
} 